// @ts-check
// Painel "Entrada de leads" (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de fluxos, seção 17): quando entram (os que
// vão atender ou os que já atendem), os blocos de entrada separados por "Ou" (filtro do lead, etapa do funil ou leads
// novos, cada um com as condições, a caixa dos importados e excluir), "+ Ou" até cinco e, no rodapé, "Definir entrada
// depois" (entrada manual).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { Checkbox } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Tag } from '../../ui/chip.js';
import { AudienceBuilder } from '../audiences/audience-builder.js';
import { createEmptyAudienceDefinition } from '../../../../shared/audiences/audience-definition.js';
import { MAXIMUM_FLOW_AUDIENCE_TRIGGERS, MAXIMUM_FLOW_TRIGGERS } from '../../../../shared/flows/flow-limits.js';
import { translateMetadataLabel, t } from '../../i18n/index.js';
import { cardField, stageOptions } from './flow-lookups.js';
import { FlowChoiceMenu } from './flow-choice-menu.js';
import { FlowSidePanel } from './flow-side-panel.js';
import { FlowField, FlowNotice, flowProblemText } from './flow-ui.js';

/**
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowEntry} FlowEntry
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowTrigger} FlowTrigger
 * @typedef {import('../../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 */

const TRIGGER_ICONS = /** @type {Record<string, string>} */ ({ AUDIENCE: 'IconFilter', STAGE_CHANGED: 'IconProgressCheck', LEAD_CREATED: 'IconUserPlus' });

/**
 * Gatilho novo de um tipo.
 * @param {'AUDIENCE' | 'STAGE_CHANGED' | 'LEAD_CREATED'} kind @param {string} [id]
 * @returns {FlowTrigger}
 */
export function createTrigger(kind, id = crypto.randomUUID()) {
  if (kind === 'AUDIENCE') return { id, kind, audience: null, includeImported: false };
  if (kind === 'STAGE_CHANGED') return { id, kind, toStages: [], fromStages: [], conditions: null, includeImported: false };
  return { id, kind, sources: [], conditions: null, includeImported: false };
}

/**
 * Chips de escolha múltipla (etapas, origens).
 * @param {{ options: Array<{ value: string, label: string, color?: string | null }>, selected: string[], onChange: (values: string[]) => void, label: string }} props
 */
function ChipChoice({ options, selected, onChange, label }) {
  return html`<div class="flow-tag-options" role="group" aria-label=${label}>
    ${options.map((option) => {
      const isChecked = selected.includes(option.value);
      return html`<button type="button" key=${option.value} class="flow-tag-option" aria-pressed=${isChecked ? 'true' : 'false'}
        onClick=${() => onChange(isChecked ? selected.filter((value) => value !== option.value) : [...selected, option.value])}>
        <${Tag} color=${option.color ?? 'gray'} text=${option.label} />
      </button>`;
    })}
  </div>`;
}

/**
 * @param {{ entry: FlowEntry, problems: FlowProblem[], onChange: (entry: FlowEntry) => void, onClose: () => void, startWithBlock?: boolean }} props
 */
export function FlowEntryPanel({ entry, problems, onChange, onClose, startWithBlock = false }) {
  // bloco sem tipo ainda ("Selecione a entrada"): só na tela até a escolha
  const [pendingBlock, setPendingBlock] = useState(startWithBlock && entry.triggers.length === 0);
  const triggers = entry.triggers;
  const audienceCount = triggers.filter((trigger) => trigger.kind === 'AUDIENCE').length;
  const canAdd = triggers.length + (pendingBlock ? 1 : 0) < MAXIMUM_FLOW_TRIGGERS;

  /** @param {FlowTrigger[]} next */
  const setTriggers = (next) => onChange({ ...entry, mode: next.length > 0 ? 'TRIGGERS' : entry.mode === 'MANUAL_ONLY' ? 'MANUAL_ONLY' : 'UNSET', triggers: next });
  /** @param {string} id @param {Partial<FlowTrigger>} patch */
  const updateTrigger = (id, patch) => setTriggers(triggers.map((trigger) => (trigger.id === id ? /** @type {FlowTrigger} */ ({ ...trigger, ...patch }) : trigger)));
  /** @param {string} kind @param {boolean} isAudienceFull */
  const kindOptions = (kind, isAudienceFull) => ['AUDIENCE', 'STAGE_CHANGED', 'LEAD_CREATED'].map((value) => ({
    value, icon: TRIGGER_ICONS[value], title: t(`flows.entry.kinds.${value}.title`), description: t(`flows.entry.kinds.${value}.description`),
    disabled: value === 'AUDIENCE' && kind !== 'AUDIENCE' && isAudienceFull, hint: value === 'AUDIENCE' && isAudienceFull ? t('flows.entry.audienceLimit', { maximum: MAXIMUM_FLOW_AUDIENCE_TRIGGERS }) : null,
  }));
  const isAudienceFull = audienceCount >= MAXIMUM_FLOW_AUDIENCE_TRIGGERS;
  const entryProblems = problems.filter((problem) => !problem.triggerId);
  const timingOptions = ['FUTURE', 'EXISTING_AND_FUTURE'].map((value) => ({
    value, icon: 'IconUsers', title: t(`flows.entry.timing.${value}`), description: t(`flows.entry.timingHint.${value}`),
  }));
  const sourceOptions = (cardField('leadSource')?.options ?? []).map((option) => ({ value: option.value, label: translateMetadataLabel(option.label), color: option.color ?? null }));

  return html`<${FlowSidePanel} title=${t('flows.entry.panelTitle')} help=${t('flows.entry.panelHelp')} onClose=${onClose} panelKey="entry" testId="flow-entry-panel"
    footer=${html`<div class="flow-entry-footer">
      <button type="button" class="flow-link-button" onClick=${() => {
        setPendingBlock(false);
        onChange({ ...entry, mode: 'MANUAL_ONLY', triggers: [] });
      }}>${t('flows.entry.later')}</button>
      <p class="flow-field-hint">${t('flows.entry.laterHint')}</p>
    </div>`}>
    ${entry.mode === 'MANUAL_ONLY' ? html`<${FlowNotice} icon="IconClick">${t('flows.entry.manualText')}<//>` : null}
    <${FlowField} label=${t('flows.entry.title')}>
      <${FlowChoiceMenu} options=${timingOptions} value=${entry.timing} placeholder=${t('flows.entry.timing.FUTURE')} ariaLabel=${t('flows.entry.title')}
        onChange=${(/** @type {string} */ timing) => onChange({ ...entry, timing: /** @type {'FUTURE' | 'EXISTING_AND_FUTURE'} */ (timing) })} />
    <//>
    ${entry.timing === 'EXISTING_AND_FUTURE' ? html`<${FlowNotice} tone="warning" icon="IconUsersGroup">${t('flows.entry.existingNotice')}<//>` : null}
    ${entryProblems.map((problem) => html`<p key=${problem.code} class="flow-field-error" role="alert">${flowProblemText(problem)}</p>`)}
    <ol class="flow-trigger-list">
      ${triggers.map((trigger, position) => {
        const triggerProblems = problems.filter((problem) => problem.triggerId === trigger.id);
        return html`<li key=${trigger.id} class="flow-trigger-item">
          ${position > 0 ? html`<div class="flow-trigger-or"><span>${t('flows.entry.or')}</span></div>` : null}
          <div class="flow-trigger-block" data-invalid=${triggerProblems.some((problem) => problem.severity === 'ERROR') ? 'true' : undefined}>
            <div class="flow-trigger-header">
              <${FlowChoiceMenu} options=${kindOptions(trigger.kind, isAudienceFull)} value=${trigger.kind} placeholder=${t('flows.entry.selectKind')}
                ariaLabel=${t('flows.entry.selectKind')}
                onChange=${(/** @type {any} */ kind) => kind !== trigger.kind && setTriggers(triggers.map((candidate) => (candidate.id === trigger.id ? createTrigger(kind, trigger.id) : candidate)))} />
              <button type="button" class="flow-icon-button" aria-label=${t('flows.entry.removeBlock')} title=${t('flows.entry.removeBlock')}
                onClick=${() => setTriggers(triggers.filter((candidate) => candidate.id !== trigger.id))}><${Icon} name="IconTrash" size=${16} /></button>
            </div>
            ${triggerProblems.map((problem) => html`<p key=${problem.code} class="flow-field-error" role="alert">${flowProblemText(problem)}</p>`)}
            ${trigger.kind === 'AUDIENCE'
              ? html`<${AudienceBuilder} key=${trigger.id} value=${trigger.audience} usage="AUDIENCE" channels=${[]} showRecipients=${false}
                  onChange=${(/** @type {any} */ audience) => updateTrigger(trigger.id, { audience })} />
                ${trigger.audience?.viewId ? html`<${FlowNotice} icon="IconRefresh">${t('flows.entry.liveViewNotice')}<//>` : null}`
              : null}
            ${trigger.kind === 'STAGE_CHANGED'
              ? html`<${FlowField} label=${t('flows.entry.toStages')} error=${triggerProblems.some((problem) => problem.details?.field === 'toStages') ? t('flows.panel.required') : null}>
                  <${ChipChoice} options=${stageOptions()} selected=${trigger.toStages} label=${t('flows.entry.toStages')}
                    onChange=${(/** @type {string[]} */ toStages) => updateTrigger(trigger.id, { toStages })} />
                <//>
                <${FlowField} label=${t('flows.entry.fromStages')} hint=${t('flows.entry.fromStagesHint')}>
                  <${ChipChoice} options=${stageOptions()} selected=${trigger.fromStages} label=${t('flows.entry.fromStages')}
                    onChange=${(/** @type {string[]} */ fromStages) => updateTrigger(trigger.id, { fromStages })} />
                <//>`
              : null}
            ${trigger.kind === 'LEAD_CREATED'
              ? html`<${FlowField} label=${t('flows.entry.sources')} hint=${t('flows.entry.sourcesHint')}>
                  <${ChipChoice} options=${sourceOptions} selected=${trigger.sources} label=${t('flows.entry.sources')}
                    onChange=${(/** @type {string[]} */ sources) => updateTrigger(trigger.id, { sources })} />
                <//>`
              : null}
            ${trigger.kind !== 'AUDIENCE'
              ? trigger.conditions
                ? html`<div class="flow-trigger-conditions">
                    <div class="flow-trigger-conditions-header">
                      <span class="flow-field-label">${t('flows.entry.conditions')}</span>
                      <button type="button" class="flow-link-button" onClick=${() => updateTrigger(trigger.id, { conditions: null })}>${t('flows.entry.removeConditions')}</button>
                    </div>
                    <${AudienceBuilder} key=${`${trigger.id}-conditions`} value=${trigger.conditions} usage="CONDITION" channels=${[]} showRecipients=${false}
                      onChange=${(/** @type {any} */ conditions) => updateTrigger(trigger.id, { conditions })} />
                  </div>`
                : html`<button type="button" class="flow-add-button" onClick=${() => updateTrigger(trigger.id, { conditions: createEmptyAudienceDefinition({ usage: 'CONDITION' }) })}>
                    <${Icon} name="IconPlus" size=${14} /><span>${t('flows.entry.addCondition')}</span>
                  </button>`
              : null}
            <label class="flow-checkbox-row">
              <${Checkbox} checked=${trigger.includeImported === true} ariaLabel=${t('flows.entry.includeImported')}
                onChange=${(/** @type {boolean} */ includeImported) => updateTrigger(trigger.id, { includeImported })} />
              <span>${t('flows.entry.includeImported')}</span>
            </label>
          </div>
        </li>`;
      })}
      ${pendingBlock
        ? html`<li class="flow-trigger-item">
            ${triggers.length > 0 ? html`<div class="flow-trigger-or"><span>${t('flows.entry.or')}</span></div>` : null}
            <div class="flow-trigger-block" data-pending="true">
              <div class="flow-trigger-header">
                <${FlowChoiceMenu} options=${kindOptions('', isAudienceFull)} value=${null} placeholder=${t('flows.entry.selectKind')} ariaLabel=${t('flows.entry.selectKind')}
                  testId="flow-entry-kind" onChange=${(/** @type {any} */ kind) => {
                    setPendingBlock(false);
                    setTriggers([...triggers, createTrigger(kind)]);
                  }} />
                <button type="button" class="flow-icon-button" aria-label=${t('flows.entry.removeBlock')} title=${t('flows.entry.removeBlock')}
                  onClick=${() => setPendingBlock(false)}><${Icon} name="IconTrash" size=${16} /></button>
              </div>
            </div>
          </li>`
        : null}
    </ol>
    ${triggers.length > 0 || pendingBlock ? html`<p class="flow-field-hint">${t('flows.entry.orHint')}</p>` : null}
    ${canAdd
      ? html`<${Button} title=${triggers.length === 0 && !pendingBlock ? t('flows.entry.select') : t('flows.entry.addOr')} icon="IconPlus" variant="secondary" size="small"
          className="flow-add-or-button" disabled=${pendingBlock} onClick=${() => setPendingBlock(true)} />`
      : html`<p class="flow-field-hint">${t('flows.entry.triggerLimit', { maximum: MAXIMUM_FLOW_TRIGGERS })}</p>`}
  <//>`;
}
